import { decodeJwtExp } from "./cleengTokenRefresh";

const CLEENG_TOKEN_STORAGE_KEY = "CLEENG_PERSISTED_TOKENS";
export const CLEENG_CHECKOUT_EXPIRED_KEY = "CLEENG_CHECKOUT_SESSION_EXPIRED";
const CLEENG_STORAGE_KEY_PATTERN = /cleeng|mssdk|mediastore/i;

let liveCleengTokens = null;
// Trello 548: the last (jwt, refreshToken) pair actually handed to the Cleeng
// SDK via setAuthTokens (or queued on window.cleeng._q). Cleeng re-validates
// the session on EVERY setAuthTokens call; the checkout page's 500ms token
// bootstrap loop plus its 5s heartbeat were pushing the identical pair up to
// ~120x a minute, which tripped Cleeng's rate limiter and rendered "Too many
// requests" inside the payment-method pane. Identical repeats are now skipped.
let queuedAuthPair = null;
// Trello 548: distinct from queuedAuthPair — set only when the pair was
// actually handed to a live/stub setAuthTokens (or reported by the widget
// itself). A pre-eval _q push is doomed because the loader replaces
// window.cleeng wholesale; the marker lets the CLEENG_SDK_LOADED replay tell
// "queued but dropped" apart from "queued and flushed".
let sdkDeliveredAuthPair = null;
let sdkAuthReplayArmed = false;
const CLEENG_LOGOUT_MESSAGE = { type: "CLEENG_LOGOUT_REQUESTED" };
// Trello 548: our own CLEENG_LOGOUT_REQUESTED (sent by requestCleengLogout
// during checkout resets) makes the widget echo AUTH_TOKENS_UPDATED
// {jwt:null, refreshToken:null} back. That echo is an acknowledgement, not a
// real session expiry — suppress it for a short window so the keep-alive /
// page handlers don't pop the "session expired" modal on a healthy reset.
const CLEENG_LOGOUT_ECHO_GRACE_MS = 8000;
let intentionalLogoutAt = 0;

// Trello 548: minimum spacing between answers to a widget's
// CLEENG_AUTH_TOKENS_REQUESTED. Every answer makes the widget re-apply the
// session and can re-fire checkout init — burst repeats within the window
// are already covered by the SDK's own provide path.
export const CLEENG_AUTH_REQUEST_MIN_INTERVAL_MS = 3000;
// Trello 548: a REPEAT ask for the identical pair on the identical iframe is
// re-answered at most this often. The widget's own recovery path is the ask
// itself (each answer re-runs its session-apply), so answering never again
// can leave a "Too many requests" pane stuck even after the bucket refills —
// while answering every ~3s keeps the limiter tripped. ~1/minute is a quiet
// enough trickle that it cannot sustain the limit on its own but still lets
// a genuinely retrying widget back in once Cleeng recovers.
export const CLEENG_AUTH_REQUEST_RETRY_MS = 60000;

const isCleengLogoutEchoWindow = () => (
  intentionalLogoutAt > 0 && Date.now() - intentionalLogoutAt < CLEENG_LOGOUT_ECHO_GRACE_MS
);

const isSameAuthPair = (pair, jwt, refreshToken) => Boolean(
  pair && pair.jwt === jwt && pair.refreshToken === refreshToken,
);

const markQueuedAuthPair = (jwt, refreshToken) => {
  queuedAuthPair = { jwt, refreshToken };
};

const markDeliveredAuthPair = (jwt, refreshToken) => {
  markQueuedAuthPair(jwt, refreshToken);
  sdkDeliveredAuthPair = { jwt, refreshToken };
};

const isCleengAuthPairDelivered = (jwt, refreshToken) => isSameAuthPair(
  sdkDeliveredAuthPair,
  jwt,
  refreshToken,
);

export const clearCleengQueuedAuthPair = () => {
  queuedAuthPair = null;
  sdkDeliveredAuthPair = null;
};

export const isCleengAuthPairQueued = (jwt, refreshToken) => isSameAuthPair(
  queuedAuthPair,
  jwt,
  refreshToken,
);

const safelyCall = (callback) => {
  try {
    callback();
  } catch (error) {
    // Best-effort browser cleanup should never block logout or checkout.
  }
};

// Meta keys that must survive SDK cache clear / reset (expire bounce guard).
// clearMatchingStorageKeys would otherwise wipe CLEENG_CHECKOUT_SESSION_EXPIRED
// right after markCleengCheckoutSessionExpired (item 45 RC-A).
const CLEENG_META_STORAGE_KEYS = new Set([
  CLEENG_CHECKOUT_EXPIRED_KEY,
]);

const clearMatchingStorageKeys = (storage) => {
  if (!storage) {
    return;
  }

  const keys = [];
  for (let index = 0; index < storage.length; index += 1) {
    keys.push(storage.key(index));
  }

  keys
    .filter((key) => {
      const name = String(key || "");
      if (CLEENG_META_STORAGE_KEYS.has(name)) {
        return false;
      }
      return CLEENG_STORAGE_KEY_PATTERN.test(name);
    })
    .forEach((key) => {
      safelyCall(() => storage.removeItem(key));
    });
};

const clearMatchingCookies = () => {
  if (typeof document === "undefined" || !document.cookie) {
    return;
  }

  const hostname = typeof window !== "undefined" && window.location
    ? window.location.hostname
    : "";
  const domainParts = hostname.split(".").filter(Boolean);
  const parentDomain = domainParts.length >= 2
    ? `.${domainParts.slice(-2).join(".")}`
    : "";

  document.cookie.split(";").forEach((rawCookie) => {
    const name = String(rawCookie).split("=")[0].trim();
    if (!name || !CLEENG_STORAGE_KEY_PATTERN.test(name)) {
      return;
    }

    const expires = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
    safelyCall(() => { document.cookie = `${name}=; ${expires}; path=/;`; });
    if (hostname) {
      safelyCall(() => { document.cookie = `${name}=; ${expires}; path=/; domain=${hostname};`; });
    }
    if (parentDomain) {
      safelyCall(() => { document.cookie = `${name}=; ${expires}; path=/; domain=${parentDomain};`; });
    }
  });
};

const requestCleengLogout = () => {
  if (typeof window === "undefined") {
    return;
  }

  // Mark before posting so the widget's {jwt:null, refreshToken:null} echo
  // is recognized as our own logout acknowledgement (Trello 548).
  intentionalLogoutAt = Date.now();

  if (window.cleeng && typeof window.cleeng.logout === "function") {
    safelyCall(() => window.cleeng.logout());
  }

  if (typeof document === "undefined") {
    return;
  }

  document
    .querySelectorAll('iframe[id^="cleeng-embedded-mediastore-sdk"], iframe[src*="cleeng.com"]')
    .forEach((iframe) => {
      if (!iframe || !iframe.contentWindow) {
        return;
      }

      safelyCall(() => iframe.contentWindow.postMessage(CLEENG_LOGOUT_MESSAGE, "*"));
    });
};

const removeCleengRuntimeNodes = () => {
  if (typeof document === "undefined") {
    return;
  }

  // Trello 548: do NOT remove the cleeng.js <script>. A re-inserted script
  // tag RE-EXECUTES the loader, which installs a second widget controller —
  // its MutationObserver + message listeners then double-render and
  // double-answer every widget, so each checkout fires the
  // /api/checkout/init + payment-method burst twice (Cleeng 429). The script
  // node is inert after its first evaluation; keeping it is what makes the
  // SDK a true singleton across remounts and SPA navigations.
  document
    .querySelectorAll('iframe[id^="cleeng-embedded-mediastore-sdk"], iframe[src*="cleeng.com"]')
    .forEach((iframe) => {
      safelyCall(() => iframe.parentNode && iframe.parentNode.removeChild(iframe));
    });
};

export const getCleengLiveAuthTokens = () => {
  if (!liveCleengTokens) {
    return null;
  }

  return {
    jwt: liveCleengTokens.jwt,
    refreshToken: liveCleengTokens.refreshToken,
  };
};

/**
 * Prefer in-memory live tokens (keep-alive / widget rotation) over the original
 * URL/React props. After long dwell Cleeng invalidates the original refresh;
 * remount must not reseed dead tokens (item 45 RC-B).
 */
export const resolveCleengAuthTokensForWidget = (propsJwt, propsRefresh) => {
  const live = getCleengLiveAuthTokens();
  if (live && live.jwt && live.refreshToken) {
    return {
      jwt: live.jwt,
      refreshToken: live.refreshToken,
      source: "live",
    };
  }

  return {
    jwt: propsJwt || null,
    refreshToken: propsRefresh || null,
    source: "props",
  };
};

export const persistCleengAuthTokens = (jwt, refreshToken) => {
  if (!jwt || !refreshToken || typeof window === "undefined") {
    return;
  }

  safelyCall(() => {
    window.sessionStorage.setItem(
      CLEENG_TOKEN_STORAGE_KEY,
      JSON.stringify({ jwt, refreshToken, timestamp: Date.now() }),
    );
  });
};

export const adoptCleengAuthTokens = (jwt, refreshToken, source = "unknown") => {
  if (!jwt || !refreshToken) {
    return false;
  }

  // Trello 548: tokens reported by the widget itself (rotation / echo via
  // onAuthTokensUpdate) are already known to the SDK — mark them delivered
  // up front so they are never echoed back through setAuthTokens, including
  // when identical to the pair we already hold.
  if (source === "widget") {
    markDeliveredAuthPair(jwt, refreshToken);
  }

  const nextTokens = {
    jwt,
    refreshToken,
    source,
    updatedAt: Date.now(),
  };

  if (
    liveCleengTokens
    && liveCleengTokens.jwt === jwt
    && liveCleengTokens.refreshToken === refreshToken
  ) {
    // Refresh the stored timestamp — the SDK reads CLEENG_PERSISTED_TOKENS as
    // its WIDGETS_LOADED fallback and skips stale entries (TTL window), so
    // repeated adopts of the same pair must keep that record fresh.
    persistCleengAuthTokens(jwt, refreshToken);
    return true;
  }

  // Trello 548: ignore a widget echo that is OLDER than the pair we already
  // hold. A widget booted with a stale auto-provide can answer
  // AUTH_TOKENS_REQUESTED with the previous pair — adopting it would regress
  // the live session and break the refresh-token chain.
  if (source === "widget" && liveCleengTokens && liveCleengTokens.jwt) {
    const liveExp = decodeJwtExp(liveCleengTokens.jwt);
    const incomingExp = decodeJwtExp(jwt);
    if (liveExp && incomingExp && incomingExp < liveExp) {
      return false;
    }
  }

  liveCleengTokens = nextTokens;
  persistCleengAuthTokens(jwt, refreshToken);
  return true;
};

export const initCleengLiveAuthTokens = (jwt, refreshToken, source = "initial") => (
  adoptCleengAuthTokens(jwt, refreshToken, source)
);

export const clearCleengLiveAuthTokens = () => {
  liveCleengTokens = null;
  // Trello 548: a fresh session/remount must re-deliver even an identical
  // pair — the dedupe marker belongs to the prior widget instance.
  clearCleengQueuedAuthPair();
  // clearCleengSessionCache re-arms this immediately via requestCleengLogout,
  // so the only observable change is for standalone clears (and tests).
  intentionalLogoutAt = 0;
};

export const queueCleengAuthTokens = (jwt, refreshToken) => {
  if (!jwt || !refreshToken || typeof window === "undefined") {
    return Promise.resolve(false);
  }

  adoptCleengAuthTokens(jwt, refreshToken, "queue");

  // Trello 548: identical pair already handed to the SDK — skip the call.
  // setAuthTokens is meant for auth-state changes, not a heartbeat cadence.
  if (isCleengAuthPairQueued(jwt, refreshToken)) {
    return Promise.resolve(true);
  }

  window.cleeng = window.cleeng || {};

  if (typeof window.cleeng.setAuthTokens === "function") {
    markDeliveredAuthPair(jwt, refreshToken);
    let result;
    try {
      result = window.cleeng.setAuthTokens({ jwt, refreshToken });
    } catch (error) {
      if (isSameAuthPair(queuedAuthPair, jwt, refreshToken)) {
        clearCleengQueuedAuthPair();
      }
      throw error;
    }
    if (result && typeof result.then === "function") {
      return result.then(() => true).catch(() => {
        // Delivery failed — unmark so the next identical call retries.
        if (isSameAuthPair(queuedAuthPair, jwt, refreshToken)) {
          clearCleengQueuedAuthPair();
        }
        return false;
      });
    }
    return Promise.resolve(true);
  }

  markQueuedAuthPair(jwt, refreshToken);
  window.cleeng._q = Array.isArray(window.cleeng._q)
    ? window.cleeng._q.filter((item) => item && item.command !== "setAuthTokens")
    : [];
  window.cleeng._q.push({
    command: "setAuthTokens",
    args: [{ jwt, refreshToken }],
  });

  // Trello 548: if the SDK hasn't evaluated yet, window.cleeng is a plain
  // object — the loader replaces it wholesale on eval, silently dropping the
  // queue we just pushed to. Arm a one-shot replay so the pair reaches the
  // real setAuthTokens as soon as the SDK posts CLEENG_SDK_LOADED
  // (CLEENG_PERSISTED_TOKENS remains the widget's own fallback at
  // WIDGETS_LOADED). Guarded by sdkDeliveredAuthPair so multiple queued calls
  // can't double-deliver.
  if (!sdkAuthReplayArmed && typeof window.cleeng.setAuthTokens !== "function") {
    sdkAuthReplayArmed = true;
    const onSdkLoaded = (event) => {
      if (!event || !event.data || event.data.type !== "CLEENG_SDK_LOADED") {
        return;
      }
      window.removeEventListener("message", onSdkLoaded);
      sdkAuthReplayArmed = false;
      const latest = getCleengLiveAuthTokens();
      if (
        latest
        && latest.jwt
        && latest.refreshToken
        && window.cleeng
        && typeof window.cleeng.setAuthTokens === "function"
        && !isCleengAuthPairDelivered(latest.jwt, latest.refreshToken)
      ) {
        markDeliveredAuthPair(latest.jwt, latest.refreshToken);
        safelyCall(() => window.cleeng.setAuthTokens({
          jwt: latest.jwt,
          refreshToken: latest.refreshToken,
        }));
      }
    };
    window.addEventListener("message", onSdkLoaded);
  }

  return Promise.resolve(false);
};

export const markCleengCheckoutSessionExpired = (backUrl = "") => {
  if (typeof window === "undefined") {
    return;
  }

  safelyCall(() => {
    window.sessionStorage.setItem(
      CLEENG_CHECKOUT_EXPIRED_KEY,
      String(backUrl || "").trim() || "1",
    );
  });
};

export const consumeCleengCheckoutSessionExpired = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const storedBackUrl = window.sessionStorage.getItem(CLEENG_CHECKOUT_EXPIRED_KEY);
  if (!storedBackUrl) {
    return null;
  }

  safelyCall(() => window.sessionStorage.removeItem(CLEENG_CHECKOUT_EXPIRED_KEY));
  return storedBackUrl;
};

export const clearCleengCheckoutSessionExpired = () => {
  if (typeof window === "undefined") {
    return;
  }

  safelyCall(() => window.sessionStorage.removeItem(CLEENG_CHECKOUT_EXPIRED_KEY));
};

export const isCleengAuthSessionExpired = (tokens = {}) => {
  if (!tokens || typeof tokens !== "object") {
    return false;
  }

  const hasJwt = Object.prototype.hasOwnProperty.call(tokens, "jwt");
  const hasRefreshToken = Object.prototype.hasOwnProperty.call(tokens, "refreshToken");

  return hasJwt && hasRefreshToken && !tokens.jwt && !tokens.refreshToken;
};

const subscribeCleengAuthTokensHandler = (handler) => {
  if (typeof window === "undefined" || typeof handler !== "function") {
    return () => {};
  }

  window.cleeng = window.cleeng || {};
  if (typeof window.cleeng.onAuthTokensUpdate === "function") {
    // Covers both states that actually deliver: the live SDK (_isLoaded true)
    // registers directly, and the pre-init stub (_isLoaded false) queues onto
    // the loader's own R array which is flushed during init (Trello 548).
    const unsubscribe = window.cleeng.onAuthTokensUpdate(handler);
    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      } else if (unsubscribe && typeof unsubscribe.unsubscribe === "function") {
        unsubscribe.unsubscribe();
      }
    };
  }

  // Trello 548: cleeng.js REPLACES window.cleeng wholesale on evaluation
  // (fresh { _q: R, ... } stub), so a subscription queued on a plain
  // { _q: [] } object created before the script ran is silently dropped —
  // the widget's AUTH_TOKENS_UPDATED echoes then go unhandled (no ACK, no
  // rotation adoption). Keep the _q push for compat, and additionally
  // re-register once the loader posts its own CLEENG_SDK_LOADED message.
  window.cleeng._q = Array.isArray(window.cleeng._q) ? window.cleeng._q : [];
  const queueItem = {
    command: "onAuthTokensUpdate",
    args: [handler],
  };
  window.cleeng._q.push(queueItem);

  let liveUnsubscribe = null;
  const onSdkLoaded = (event) => {
    if (!event || !event.data || event.data.type !== "CLEENG_SDK_LOADED") {
      return;
    }
    window.removeEventListener("message", onSdkLoaded);
    if (
      window.cleeng
      && typeof window.cleeng.onAuthTokensUpdate === "function"
    ) {
      liveUnsubscribe = window.cleeng.onAuthTokensUpdate(handler);
    }
  };
  window.addEventListener("message", onSdkLoaded);

  return () => {
    window.removeEventListener("message", onSdkLoaded);
    if (window.cleeng && Array.isArray(window.cleeng._q)) {
      window.cleeng._q = window.cleeng._q.filter((item) => item !== queueItem);
    }
    if (typeof liveUnsubscribe === "function") {
      liveUnsubscribe();
    } else if (liveUnsubscribe && typeof liveUnsubscribe.unsubscribe === "function") {
      liveUnsubscribe.unsubscribe();
    }
  };
};

export const subscribeCleengAuthTokenStore = ({
  onRotation,
  onExpired,
} = {}) => {
  if (typeof window === "undefined") {
    return () => {};
  }

  let active = true;
  const handler = (tokens = {}) => {
    if (!active) {
      return;
    }

    if (isCleengAuthSessionExpired(tokens)) {
      // Trello 548: {jwt:null, refreshToken:null} echoes caused by our own
      // reset-time CLEENG_LOGOUT_REQUESTED are acknowledgements, not real
      // expiries — swallow them inside the grace window.
      if (!isCleengLogoutEchoWindow() && typeof onExpired === "function") {
        onExpired(tokens);
      }
      return;
    }

    if (tokens.jwt && tokens.refreshToken) {
      const adopted = adoptCleengAuthTokens(tokens.jwt, tokens.refreshToken, "widget");
      if (adopted && typeof onRotation === "function") {
        onRotation(tokens);
      }
    }
  };

  // Trello 548: the SDK's onAuthTokensUpdate returns no unsubscribe — the
  // inner listener stays bound for the rest of the page session, so
  // `active` is the only thing stopping a destroyed keep-alive or a
  // remounted checkout effect from still adopting tokens and firing
  // onExpired/onRotation on every widget echo.
  const unsubscribe = subscribeCleengAuthTokensHandler(handler);
  return () => {
    active = false;
    unsubscribe();
  };
};

export const subscribeCleengAuthTokensUpdate = (callback) => {
  if (typeof window === "undefined" || typeof callback !== "function") {
    return () => {};
  }

  let active = true;
  const handler = (tokens) => {
    // Trello 548: same logout-echo suppression as subscribeCleengAuthTokenStore.
    if (active && isCleengAuthSessionExpired(tokens) && !isCleengLogoutEchoWindow()) {
      callback(tokens);
    }
  };

  const unsubscribe = subscribeCleengAuthTokensHandler(handler);
  return () => {
    active = false;
    unsubscribe();
  };
};

/**
 * Clear Cleeng SDK browser state (localStorage + sessionStorage + queued
 * SDK commands) so the next checkout treats the user as a fresh customer.
 *
 * Bug: Cleeng widget caches JWT/customer in browser storage. After
 * MFS logout, the previous customer's Cleeng session persists, so when
 * a new MFS user clicks checkout, Cleeng identifies them as the prior
 * customer that already has access → "good news. your account already
 * gives you access..." (Trello WEB card item 14, 6 May 26).
 *
 * Call this:
 *   - On MFS logout (clear stale state immediately)
 *   - Before redirecting to /payment/cleeng/[id] (defense in depth)
 */
export const clearCleengSessionCache = () => {
  if (typeof window === "undefined") {
    return;
  }

  clearCleengLiveAuthTokens();
  requestCleengLogout();
  safelyCall(() => clearMatchingStorageKeys(window.localStorage));
  safelyCall(() => clearMatchingStorageKeys(window.sessionStorage));

  // Drop queued SDK commands (setAuthTokens, etc.) from prior session.
  if (window.cleeng && Array.isArray(window.cleeng._q)) {
    window.cleeng._q = [];
  }

  // Also clear cookies on the current document that match Cleeng/mssdk —
  // some Cleeng widget builds drop a customer-id cookie on mfs domain.
  clearMatchingCookies();
};

/**
 * Ensure the cleeng.js loader script is present in <head> — appends it once
 * and reuses the existing tag afterwards. Re-adding a script element always
 * re-executes it, and every execution installs an additional widget
 * controller with its own MutationObserver/message listeners, so reuse is
 * what keeps the SDK a singleton (Trello 548).
 */
export const ensureCleengSdkScript = (scriptUrl) => {
  if (typeof document === "undefined" || !scriptUrl) {
    return null;
  }

  const src = String(scriptUrl).trim();
  const existing = document.querySelector("script[data-cleeng-sdk=\"1\"]")
    || document.querySelector("script[src*=\"cleeng.js\"]");
  if (existing) {
    return existing;
  }

  const scriptEl = document.createElement("script");
  scriptEl.type = "module";
  scriptEl.src = src;
  scriptEl.async = true;
  scriptEl.dataset.cleengSdk = "1";
  document.head.appendChild(scriptEl);
  return scriptEl;
};

/**
 * Force Cleeng checkout-session reset. Clears every piece of auth state
 * (Cleeng storage, live token memory, queued pairs) and removes the widget
 * iframe so the next mount starts as a fresh customer.
 *
 * Trello 548: the window.cleeng facade and the cleeng.js <script> are kept
 * on purpose. Every evaluation of cleeng.js installs an additional widget
 * controller whose MutationObserver and message listeners stay attached for
 * the rest of the page session — with two controllers, the widget div gets
 * rendered twice (each clobbering the other's iframe) and every checkout
 * init/payment-method call fires twice, which is the intermittent "Too many
 * requests" pane. Auth state is already reset via clearCleengSessionCache
 * (logout + storage wipe), so the surviving controller renders the next
 * [data-cleeng-widget] node with clean state.
 */
export const resetCleengSdkRuntime = () => {
  if (typeof window === "undefined") {
    return;
  }

  clearCleengSessionCache();
  removeCleengRuntimeNodes();
};
